import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, BookOpen, DollarSign, PieChart, TrendingUp, Eye, EyeOff } from 'lucide-react';

const CommunityMetrics = ({ stats, isAdmin = false, showToggle = false, isVisible = true, onToggleVisibility = null, theme = 'dark' }) => {
  const isLight = theme === 'light';
  
  if (!stats) return null;

  const totalSubscribers = stats.total_active || 0;
  const totalAuthors = stats.total_authors || 0;
  const totalAudioLectores = stats.total_consumers || 0;
  
  // Calculate pool value per subscriber
  const monthlySubscription = 199;
  const poolPercent = 0.42;
  const monthlyPool = totalSubscribers * monthlySubscription * poolPercent;
  const poolValuePerSubscriber = totalSubscribers > 0 ? (monthlySubscription * poolPercent) : 0;
  
  // Author's participation (equal share)
  const authorParticipation = totalAuthors > 0 ? (100 / totalAuthors).toFixed(1) : 0;

  const formatCurrency = (amount) => {
    return '$' + Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (!isVisible && !isAdmin) return null;

  return (
    <Card className={isLight ? "bg-white rounded-2xl shadow-sm border border-gray-100" : "card-viubux-dark border-blue-500/30"}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className={`gotham-bold text-xl flex items-center gap-3 ${isLight ? 'text-gray-900' : 'text-white'}`}>
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Métricas de la Comunidad
            <span className={`text-xs px-2 py-1 rounded-full ${isLight ? 'bg-blue-100 text-blue-600' : 'bg-blue-500/20 text-blue-300'}`}>Visión Global</span>
          </CardTitle>
          {showToggle && onToggleVisibility && (
            <button
              onClick={onToggleVisibility}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                isLight 
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-600' 
                  : 'bg-white/5 hover:bg-white/10 text-white/70'
              }`}
              title={isVisible ? "Ocultar para autores" : "Mostrar para autores"}
            >
              {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {isVisible ? "Visible" : "Oculto"}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-4 gap-4">
          {/* Total Audio-Lectores */}
          <div className={`p-4 rounded-xl ${isLight ? 'bg-green-50 border border-green-100' : 'bg-gradient-to-br from-green-500/10 to-transparent border border-green-500/20'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${isLight ? 'bg-green-100' : 'bg-green-500/20'}`}>
                <Users className="w-5 h-5 text-green-500" />
              </div>
              <span className={`text-sm ${isLight ? 'text-gray-500' : 'text-white/60'}`}>Audio-Lectores</span>
            </div>
            <p className={`gotham-bold text-3xl ${isLight ? 'text-gray-900' : 'text-white'}`}>{totalAudioLectores.toLocaleString()}</p>
            <p className="text-green-500 text-xs mt-1">En la red</p>
          </div>

          {/* Total Autores */}
          <div className={`p-4 rounded-xl ${isLight ? 'bg-violet-50 border border-violet-100' : 'bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${isLight ? 'bg-violet-100' : 'bg-purple-500/20'}`}>
                <BookOpen className="w-5 h-5 text-violet-500" />
              </div>
              <span className={`text-sm ${isLight ? 'text-gray-500' : 'text-white/60'}`}>Autores</span>
            </div>
            <p className={`gotham-bold text-3xl ${isLight ? 'text-gray-900' : 'text-white'}`}>{totalAuthors.toLocaleString()}</p>
            <p className="text-violet-500 text-xs mt-1">En el sello</p>
          </div>

          {/* Pool Value per Subscriber */}
          <div className={`p-4 rounded-xl ${isLight ? 'bg-amber-50 border border-amber-100' : 'bg-gradient-to-br from-yellow-500/10 to-transparent border border-yellow-500/20'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${isLight ? 'bg-amber-100' : 'bg-yellow-500/20'}`}>
                <DollarSign className="w-5 h-5 text-amber-500" />
              </div>
              <span className={`text-sm ${isLight ? 'text-gray-500' : 'text-white/60'}`}>Valor "Acción" Pool</span>
            </div>
            <p className={`gotham-bold text-3xl ${isLight ? 'text-gray-900' : 'text-white'}`}>{formatCurrency(poolValuePerSubscriber)}</p>
            <p className="text-amber-500 text-xs mt-1">MXN por suscriptor</p>
          </div>

          {/* Author Participation */}
          <div className={`p-4 rounded-xl ${isLight ? 'bg-blue-50 border border-blue-100' : 'bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${isLight ? 'bg-blue-100' : 'bg-blue-500/20'}`}>
                <PieChart className="w-5 h-5 text-blue-500" />
              </div>
              <span className={`text-sm ${isLight ? 'text-gray-500' : 'text-white/60'}`}>Tu Participación</span>
            </div>
            <p className={`gotham-bold text-3xl ${isLight ? 'text-gray-900' : 'text-white'}`}>{authorParticipation}%</p>
            <p className="text-blue-500 text-xs mt-1">Del fondo mensual</p>
          </div>
        </div>

        {/* Pool Summary */}
        <div className={`mt-4 p-4 rounded-xl ${isLight ? 'bg-gray-50' : 'bg-black/20'}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className={`text-sm ${isLight ? 'text-gray-500' : 'text-white/60'}`}>Pool Mensual Total (42%)</p>
              <p className="gotham-bold text-2xl text-violet-500">{formatCurrency(monthlyPool)} MXN</p>
            </div>
            <div className="text-right">
              <p className={`text-sm ${isLight ? 'text-gray-500' : 'text-white/60'}`}>Suscriptores Activos</p>
              <p className="gotham-bold text-2xl text-green-500">{totalSubscribers.toLocaleString()}</p>
            </div>
          </div>
          <p className={`text-xs mt-3 verdana-regular ${isLight ? 'text-gray-400' : 'text-white/40'}`}>
            💡 A medida que la tribu crece, el valor de cada "acción" del pool aumenta para todos los autores activos.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommunityMetrics;
